// `capture_items` PATCH (updateItem) dispatch.
//
// Extracted from connector.ts (the 500-line lint:max-lines cap left no
// room there). dispatchCrudEntry delegates every capture_items PATCH here;
// the connector re-exports `_resetLwwAttempts` so existing test imports
// (`from '../connector'`) keep working.
//
// Three behaviours, in priority order:
//
//   1. RE-HOME (this file's reason to grow). When an earlier createItem PUT
//      collided on its (session, space, asset) scope, its carve-out re-homed
//      the count onto the server's authoritative row at a DIFFERENT id,
//      DELETEd the local orphan, and recorded orphan→authoritative in the
//      module-level connector-rehome-registry.ts. The +/- increments the
//      surveyor captured against the orphan are queued as their own PATCH
//      entries; without re-homing they PUT the now-deleted id → 404 → the
//      count is dropped (silent contractual data-loss, since it lived only
//      on this device). When the entry's id is in the registry we instead
//      PUT the increment onto the authoritative row, so the count is
//      preserved. The registry is module-level (not per-batch) so this works
//      even when the create and its increments land in DIFFERENT CRUD
//      batches — the >100-op offline-session case (Codex P1 on PR #630).
//
//   2. LWW CONFLICT (failed_precondition). The normal stale-version path
//      for a row that DOES exist server-side: reapply the user's intent on
//      top of the authoritative version once, then reconcile to the bare
//      authoritative state on a second consecutive conflict (the LWW
//      contract under sustained contention — docs/buildspec/capture/
//      10-api.md §4, 20-ui.md §8.1/§8.7). Match on `err.code ===
//      'failed_precondition'` only — Encore's APIError.failedPrecondition
//      maps to HTTP 400 with that code (backend update-item.ts), so a
//      status === 409 guard would miss it and wedge the queue.
//
//   3. NOT_FOUND QUARANTINE. A PATCH against a server-missing id that was NOT
//      re-homed (e.g. the createItem 404'd on an absent FK parent, so there
//      is no authoritative row to re-home onto). Throwing would wedge the
//      whole upload queue (uploadData acks only once every entry succeeds),
//      so PRESERVE the count in the device-local quarantine (never silently
//      drop — connector-quarantine.ts), ack so the queue drains, and emit
//      `quarantined` so the UI can offer recovery. All other 4xx/5xx throw.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'

import { ApiError, apiFetch, isNotFoundError } from '../api'

import {
  extractAuthoritativeRow,
  reapplyCaptureItemIntent,
} from './connector-lww-reapply'
import { canonicalizeForUpload } from './quantity-canonical'
import { quarantineUnsyncableCount } from './connector-quarantine'
import {
  getCaptureItemRehome,
  recordCaptureItemRehome,
  type CaptureItemRehomeTarget,
} from './connector-rehome-registry'
import { authoritativeMatchesLocalIntent } from './connector-self-inflicted-conflict'
import { _emitUploaded } from './upload-events'

/**
 * Module-level per-row LWW bookkeeping, keyed by row id. The genuine-conflict
 * reapply is now bounded INLINE (reapply-once-then-yield, connector-lww-reapply.ts)
 * rather than across upload cycles, so this map carries no cross-cycle state in
 * the steady path — terminal paths (a successful PUT, the self-inflicted ACK)
 * clear any row's entry. It is retained, with the exported `_resetLwwAttempts`,
 * because ~45 connector test suites reset it in `afterEach` to isolate `it`
 * blocks, and so any future cross-cycle retry state has a single home that is
 * already cleared on every terminal path.
 */
const lwwAttempts = new Map<string, number>()
export function _resetLwwAttempts(): void {
  lwwAttempts.clear()
}

interface LocalRecoveryFields {
  /** The version the backend LWW guard expects (the op did not bump it). */
  readonly syncVersion: number
  /** Count fields, so a recovered PUT can preserve any the thin op omitted. */
  readonly isZeroConfirmed: number | null
  readonly notes: string | null
}

/**
 * The local capture_items row's current version + count fields, or undefined if
 * the row is gone (or its version is unusable). Recovers what a COUNT-BEARING
 * PATCH dropped via onlyWhenChanged (a local UPDATE that left columns UNCHANGED →
 * PowerSync trims them from the op), so the count can LAND via the normal LWW PUT
 * instead of wedging the queue on a versionless body — AND so a landed PUT
 * preserves an unchanged is_zero_confirmed / notes from the row rather than
 * clearing them to false / null from the thin op (Codex P2). The stored version
 * is exactly what the backend LWW guard expects; a stale read just yields a
 * `failed_precondition` the LWW path reconciles.
 */
async function readLocalCaptureItemForRecovery(
  db: PowerSyncWriteDb,
  id: string,
): Promise<LocalRecoveryFields | undefined> {
  const rows = await db.getAll<{
    sync_version: number | null
    is_zero_confirmed: number | null
    notes: string | null
  }>(
    'SELECT sync_version, is_zero_confirmed, notes FROM capture_items WHERE id = ?',
    [id],
  )
  const row = rows[0]
  if (row === undefined || typeof row.sync_version !== 'number') return undefined
  return {
    syncVersion: row.sync_version,
    isZeroConfirmed: row.is_zero_confirmed,
    notes: row.notes,
  }
}

/**
 * Dispatch a single `capture_items` PATCH. See the file header for the
 * three behaviours (re-home / LWW conflict / not_found drop).
 */
export async function dispatchCaptureItemPatch(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
): Promise<void> {
  const data = entry.opData ?? {}

  // (1) RE-HOME: the local orphan this PATCH targets was re-homed onto an
  // authoritative row (in this batch or an earlier one). The orphan row is
  // already deleted, so the increment can only land on the authoritative id.
  const rehome = getCaptureItemRehome(entry.id)
  if (rehome !== undefined) {
    return dispatchRehomedIncrement(entry, db, rehome, data)
  }

  // Pick the right syncVersion for the body. The backend's
  // applyVersionGuard validates against the row version the user's
  // edit applied on top of — which is NOT always the same source.
  //
  // Normal increment (every +/- tap): the adapter writes optimistic
  //   N+1 to local, so opData = previousValues + 1. The pre-bump N
  //   in previousValues is what the backend expects.
  //
  // LWW catchup retry (after a failed_precondition response):
  //   handleLwwConflict's local UPDATE wrote the AUTHORITATIVE
  //   version directly — a multi-version jump from the previous
  //   local (e.g., opData=5, previousValues=2). The next upload
  //   must send the authoritative (5) so the backend sees us
  //   catching up to its state. Sending previousValues here would
  //   just trigger another conflict and the 2nd-attempt path would
  //   drop the user's edit (Codex P1 — without this branch, every
  //   real concurrent-edit conflict loses the user's intent on the
  //   very first retry).
  //
  // Discriminator is structural — `opSync === prevSync + 1` is
  //   precisely the bump pattern the adapter writes. A multi-version
  //   jump means handleLwwConflict's UPDATE (or any other future
  //   path that writes a non-incremental sync_version) and is
  //   handled by sending opData directly.
  //
  // Defensive fallback (no previousValues): assume the normal bump
  //   pattern and derive N from opData - 1. Better than crashing
  //   uploadData on a misconfigured schema.
  const previousValues = entry.previousValues ?? {}
  const opSync = data['sync_version']
  const prevSync = previousValues['sync_version']
  let callerSyncVersion: number | undefined
  if (typeof prevSync === 'number' && typeof opSync === 'number') {
    callerSyncVersion = opSync === prevSync + 1 ? prevSync : opSync
  } else if (typeof opSync === 'number') {
    callerSyncVersion = opSync - 1
  } else if (typeof prevSync === 'number') {
    callerSyncVersion = prevSync
  } else {
    callerSyncVersion = undefined
  }

  // PUT-body count fields from the (possibly thin) opData. onlyWhenChanged trims an
  // UNCHANGED is_zero_confirmed / notes from the op, so when absent they coerce to
  // false / null and CLEAR a confirmed-zero flag (ADR-021 export gating) or a note the
  // row still holds (Codex P2). `quantity` is already safe (undefined → drizzle leaves
  // the column). The field-recovery block below restores any OMITTED field from the
  // local row — for a VERSION-BEARING op as much as a versionless one.
  let bodyIsZeroConfirmed = data['is_zero_confirmed'] === 1
  let bodyNotes: string | null =
    (data['notes'] as string | null | undefined) ?? null
  const zeroOmitted = !('is_zero_confirmed' in data)
  const notesOmitted = !('notes' in data)

  // SAFETY NET — never PUT a capture_items op without a syncVersion (the >30-PUT
  // batch-wedge PERMANENCE; memory put-batch-wedge-prod). callerSyncVersion is
  // undefined only when onlyWhenChanged (schema.ts) dropped sync_version because a
  // local UPDATE left it UNCHANGED. PUTting it omits syncVersion → Encore's request
  // decoder 400s "missing field syncVersion" → the connector rethrows → the WHOLE
  // device queue wedges. Two shapes, both handled before the PUT:
  //
  // (a) NO count rides the op (the classic value-no-op handleLwwConflict re-queue):
  //     nothing to upload → ACK to DRAIN. Pinned by
  //     lint-powersync-patch-requires-sync-version — the quantity conjunct is
  //     load-bearing: acking on `callerSyncVersion === undefined` ALONE would
  //     silently drop the count-bearing op handled in (b).
  if (callerSyncVersion === undefined && data['quantity'] === undefined) {
    _emitUploaded(entry.table, entry.id)
    return
  }
  // Read AT MOST ONCE — shared by branch (b) and the field-recovery block below.
  let local: LocalRecoveryFields | undefined
  // (b) The op DOES carry a count but lost its version: the LWW yield path
  //     (connector-lww-reapply.ts) wrote the AUTHORITATIVE quantity + version to the
  //     local row, and when that version equalled the row's, onlyWhenChanged trimmed
  //     sync_version (the Wellington `a2f38190` prod wedge — PUTting it omits
  //     syncVersion → Encore 400 → wedge; ack-dropping it would LOSE the count).
  //     RECOVER the version from the local row so the count LANDS via the PUT below;
  //     if the row is gone the count can never sync, so PRESERVE it in quarantine
  //     (never a silent drop). NOT an ack-drain, so the (a) gate's silent-drop ban holds.
  if (callerSyncVersion === undefined) {
    local = await readLocalCaptureItemForRecovery(db, entry.id)
    if (local === undefined) {
      return quarantineUnsyncableCount(
        entry,
        db,
        new Error(
          'capture_items PATCH carries a count but no syncVersion and the local row is gone',
        ),
        data,
        'powersync_capture_item_patch_missing_version_quarantine',
      )
    }
    callerSyncVersion = local.syncVersion
  }

  // RECOVER trimmed count fields — for a VERSION-BEARING op too (the gap this closes).
  // onlyWhenChanged drops an UNCHANGED is_zero_confirmed / notes whether or not it also
  // trimmed sync_version, so a notes-only edit of a confirmed-zero row keeps its version
  // yet omits is_zero_confirmed; the old MISSING-version gate let the PUT clear the flag
  // on the common ONLINE edit. Read the row only if branch (b) didn't, then override each
  // OMITTED field. STRICTLY SAFER: an omitted field is only ever re-asserted to the value
  // the row still holds (a present field keeps the user's intent; a gone row changes
  // nothing).
  if ((zeroOmitted || notesOmitted) && local === undefined) {
    local = await readLocalCaptureItemForRecovery(db, entry.id)
  }
  if (local !== undefined) {
    if (zeroOmitted) bodyIsZeroConfirmed = local.isZeroConfirmed === 1
    if (notesOmitted) bodyNotes = local.notes
  }

  try {
    await apiFetch(`/capture/items/${encodeURIComponent(entry.id)}`, {
      method: 'PUT',
      json: {
        // #801 canonicalises the quantity (trailing-dot/"" → valid) AND #804
        // preserves an unchanged is_zero_confirmed / notes the thin op trimmed.
        quantity: canonicalizeForUpload(data['quantity']),
        isZeroConfirmed: bodyIsZeroConfirmed,
        notes: bodyNotes,
        syncVersion: callerSyncVersion,
      },
    })
    // Success — clear any prior conflict bookkeeping for this row,
    // then emit an upload event so any subscribed UI / telemetry
    // can flip a row's "still uploading" affordance to "uploaded".
    // Emitted ONLY on the actual-PUT-succeeded branch — the LWW
    // reapply path returns without an emit because it didn't
    // actually upload anything yet (the fresh PATCH it queued will
    // produce its own upload event when it lands).
    lwwAttempts.delete(entry.id)
    _emitUploaded(entry.table, entry.id)
    return
  } catch (err) {
    if (err instanceof ApiError && err.code === 'failed_precondition') {
      await handleLwwConflict(entry, err, db)
      return
    }
    if (isNotFoundError(err)) {
      // (3) NOT_FOUND QUARANTINE. The row this PATCH targets does not exist
      // server-side and was not re-homed, so the update can NEVER land and
      // throwing would wedge the whole upload queue (uploadData acks only
      // once every entry succeeds — every later mutation for every table
      // blocks behind this one). isNotFoundError covers a code-less 404 too (a
      // proxy/CDN 404 with no Encore code — H3/#694), so it quarantines inline
      // here rather than dropping to the generic backstop. This happens when an
      // earlier createItem
      // for the same local id 404'd on an absent FK parent (no
      // authoritative row to re-home onto). The count lives ONLY on this
      // device, so a silent drop is contractual data-loss: PRESERVE the full
      // payload in the device-local quarantine FIRST, THEN remove the orphan
      // + ack so the queue drains, and emit `quarantined` so the UI can offer
      // recovery. Mirrors the create-item not_found carve-out.
      return quarantineUnsyncableCount(
        entry,
        db,
        err,
        data,
        'powersync_capture_item_patch_not_found_drop',
      )
    }
    throw err
  }
}

/**
 * (1) Apply a re-homed increment onto the authoritative row.
 *
 * The createItem already_exists carve-out re-homed `entry.id` onto
 * `rehome.id` and DELETEd the local orphan, so the surveyor's count can only
 * land on the authoritative row. PUT the increment there at the version we
 * recorded; on a stale-version conflict (a concurrent editor bumped the row
 * in the reconnect window) retry ONCE at the server's authoritative version,
 * then yield per LWW. On not_found the authoritative row is gone too — drop
 * with telemetry.
 *
 * Two attempts total mirror the reapply-once-then-yield bound of the
 * normal LWW path, but applied directly to the server: the local orphan is
 * already deleted, so handleLwwConflict's "UPDATE local then requeue"
 * strategy can't apply here (the UPDATE would no-op against a missing row).
 */
async function dispatchRehomedIncrement(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  rehome: CaptureItemRehomeTarget,
  data: Record<string, unknown>,
): Promise<void> {
  const payload = {
    quantity: canonicalizeForUpload(data['quantity']),
    isZeroConfirmed: data['is_zero_confirmed'] === 1,
    notes: data['notes'] ?? null,
  }

  // H17: a thin increment PATCH carries only quantity/is_zero_confirmed/notes/
  // sync_version, and the local capture_items row was DELETEd by the createItem
  // carve-out that re-homed this orphan — so if this increment must be
  // quarantined below, quarantineUnsyncableCount can recover the FK context from
  // NEITHER opData NOR the (gone) local row, and the orphan row would get NULL
  // FKs → the recovery surface (rescue-held-captures.ts LEFT JOINs on space_id /
  // capture_session_id) could not place the count. Merge the authoritative row's
  // FK/capture context (recorded by the carve-out at re-home time) UNDER the
  // count fields so EVERY quarantine path below records WHERE the count belongs.
  // Count fields win (spread last); the context keys (FK ids + captured_at) are
  // disjoint from quantity/is_zero_confirmed/notes/sync_version, so nothing is
  // clobbered. `rehome.context` is undefined for a legacy/restart registry entry
  // → degrades to the prior thin payload, never a crash.
  const quarantineData = { ...rehome.context, ...data }

  // Attempt 1 — the version recorded by the createItem carve-out.
  try {
    await putRehomedIncrement(entry, rehome.id, rehome.syncVersion, payload)
    return
  } catch (err) {
    // isNotFoundError FIRST (on the raw unknown), so its type guard narrows to
    // ApiError in the true branch without collapsing the still-unknown false
    // branch to `never` — then the instanceof guard below narrows the rest.
    if (isNotFoundError(err)) {
      // not_found OR a code-less 404 (proxy/CDN 404, no Encore code — H3/#694).
      return quarantineUnsyncableCount(
        entry,
        db,
        err,
        quarantineData,
        'powersync_capture_item_rehome_target_not_found_drop',
      )
    }
    if (!(err instanceof ApiError)) throw err
    if (err.code !== 'failed_precondition') throw err
    // Stale recorded version — retry once at the authoritative version the
    // conflict surfaced. Absent payload → cannot retry safely, so yield.
    const authVersion = readAuthoritativeSyncVersion(err)
    if (authVersion === null) {
      return quarantineUnsyncableCount(
        entry,
        db,
        err,
        quarantineData,
        'powersync_capture_item_rehome_conflict_drop',
      )
    }
    // Attempt 2 — authoritative version.
    try {
      await putRehomedIncrement(entry, rehome.id, authVersion, payload)
      return
    } catch (retryErr) {
      if (isNotFoundError(retryErr)) {
        // not_found OR a code-less 404 (proxy/CDN 404, no Encore code — H3/#694).
        return quarantineUnsyncableCount(
          entry,
          db,
          retryErr,
          quarantineData,
          'powersync_capture_item_rehome_target_not_found_drop',
        )
      }
      if (
        retryErr instanceof ApiError &&
        retryErr.code === 'failed_precondition'
      ) {
        // Second consecutive conflict — a faster concurrent editor owns the
        // row. Under LWW the surveyor's edit yields; drop with telemetry
        // rather than loop the batch forever.
        return quarantineUnsyncableCount(
          entry,
          db,
          retryErr,
          quarantineData,
          'powersync_capture_item_rehome_conflict_drop',
        )
      }
      throw retryErr
    }
  }
}

/**
 * PUT a re-homed increment onto the authoritative row and refresh the
 * registry with the row's new version, so a SECOND queued increment for the
 * same orphan (this batch or a later one) lands first try instead of
 * conflicting. Reads the bumped version off the updateItem response
 * (CaptureItemRow.syncVersion); falls back to +1 if absent.
 */
async function putRehomedIncrement(
  entry: CrudEntry,
  targetId: string,
  syncVersion: number,
  payload: { quantity: unknown; isZeroConfirmed: boolean; notes: unknown },
): Promise<void> {
  const updated = await apiFetch<{ syncVersion?: number }>(
    `/capture/items/${encodeURIComponent(targetId)}`,
    {
      method: 'PUT',
      json: {
        quantity: payload.quantity,
        isZeroConfirmed: payload.isZeroConfirmed,
        notes: payload.notes,
        syncVersion,
      },
    },
  )
  const nextVersion =
    typeof updated?.syncVersion === 'number'
      ? updated.syncVersion
      : syncVersion + 1
  recordCaptureItemRehome(entry.id, { id: targetId, syncVersion: nextVersion })
  _emitUploaded(entry.table, entry.id)
}

/**
 * Pull `details.authoritative.syncVersion` out of a failed_precondition
 * error body (camelCase). Returns null if missing/malformed. Delegates to the
 * shared `extractAuthoritativeRow` so the rehome and reapply paths parse the
 * conflict body identically.
 */
function readAuthoritativeSyncVersion(err: ApiError): number | null {
  return extractAuthoritativeRow(err)?.syncVersion ?? null
}

/**
 * (2) LWW conflict on a row that EXISTS server-side. Two outcomes:
 *
 *   - SELF-INFLICTED (the authoritative row already equals this device's local
 *     intent): the device re-sent its OWN already-applied edit after a sibling
 *     429 forced the all-or-nothing batch to retry. ACK and drain (the >30-PUT
 *     batch-wedge ROOT; memory put-batch-wedge-prod).
 *   - GENUINE (authoritative VALUE differs): re-apply the surveyor's intent at
 *     the authoritative version so the count reaches the server (real
 *     last-write-wins, A wins) — delegated to `reapplyCaptureItemIntent`, a
 *     direct PUT, NOT the old "local UPDATE then re-queue" that silently dropped
 *     the count when the reapply degenerated to a no-op op (memory
 *     put-batch-wedge-prod "INDEPENDENT VERIFICATION of PR #798").
 */
async function handleLwwConflict(
  entry: CrudEntry,
  err: ApiError,
  db: PowerSyncWriteDb,
): Promise<void> {
  const auth = extractAuthoritativeRow(err)
  if (auth === null) {
    // Stale-version error without a usable authoritative row (no row, or a
    // non-numeric syncVersion) — should never happen with the current backend,
    // but if it does we MUST throw rather than ack. Acking would be the
    // silent-data-loss bug.
    throw err
  }

  // SELF-INFLICTED CONFLICT ACK (#798). A failed_precondition whose
  // authoritative row ALREADY equals this device's intent is its OWN
  // already-applied edit re-sent after a sibling 429 forced a batch retry — not
  // a real cross-device divergence. The server holds the count, so ACK and
  // drain. A GENUINE conflict differs in VALUE → false → reapply.
  if (await authoritativeMatchesLocalIntent(db, entry.id, auth.row)) {
    lwwAttempts.delete(entry.id)
    _emitUploaded(entry.table, entry.id)
    return
  }

  // GENUINE conflict — reapply the surveyor's intent directly at the
  // authoritative version (real LWW: A wins). See connector-lww-reapply.ts.
  await reapplyCaptureItemIntent(entry, db, auth)
}
